import * as React from "react";
import { motion, useReducedMotion } from "motion/react";
import {
  CalendarDays,
  CalendarHeart,
  Home,
  LogOut,
  PartyPopper,
  UserRoundX,
  Wallet,
} from "lucide-react";
import { Button, cn } from "@eduviaos/ui-kit";
import { BottomNav, type NavItem } from "../../components/BottomNav";
import { SideNav } from "../../components/SideNav";
import { InstitutionMark } from "../../components/InstitutionMark";
import { SectionCard } from "../../components/Section";
import { verticalLabel, type Institution } from "../../lib/institution";
import { EASE_OUT } from "../../lib/motion";
import { useSession } from "../../lib/sessionContext";
import { useTeamProfile } from "../../lib/team/api";
import { TeamAttendance } from "./TeamAttendance";
import { TeamHolidays, TeamLeaves } from "./TeamLeaves";
import { TeamPay } from "./TeamPay";
import { TeamToday } from "./TeamToday";

/**
 * Phase SC-22d — the portal's Team persona. Same portal URL as learners;
 * shown when the signed-in user is a teacher / staff / admin. Sections
 * follow `GET /api/me/team-profile`: Pay only when the centre shows pay,
 * Leave requests only when the centre allows them.
 */

type Section = "today" | "attendance" | "pay" | "leave" | "holidays";

function NotSetUp({ onSignOut, reason }: { onSignOut: () => void; reason: "member" | "module" }) {
  return (
    <SectionCard className="mx-auto mt-10 flex max-w-md flex-col items-center gap-4 py-10 text-center">
      <span className="bg-muted text-muted-foreground grid size-14 place-items-center rounded-2xl">
        <UserRoundX aria-hidden="true" className="size-7" />
      </span>
      <div className="space-y-1.5">
        <p className="font-display text-lg font-semibold">
          {reason === "member"
            ? "You're not on the team list yet"
            : "Team check-in isn't set up yet"}
        </p>
        <p className="text-muted-foreground mx-auto max-w-xs text-sm">
          {reason === "member"
            ? "Ask your centre to link your account to your team profile. Then your attendance and pay show up here."
            : "Your centre hasn't set up team attendance and pay yet. Ask them when it will be ready."}
        </p>
      </div>
      <Button variant="outline" onClick={onSignOut} className="rounded-2xl">
        <LogOut aria-hidden="true" className="size-4" />
        Sign out
      </Button>
    </SectionCard>
  );
}

export function TeamApp({ institution }: { institution: Institution }) {
  const reduce = useReducedMotion();
  const { user, signOut } = useSession();
  const profileQuery = useTeamProfile();
  const profile = profileQuery.data;
  const [section, setSection] = React.useState<Section>("today");
  const firstName = (profile?.name ?? user?.name ?? "there").split(" ")[0] ?? "there";

  const items = React.useMemo<NavItem[]>(() => {
    const out: NavItem[] = [
      { id: "today", label: "Today", icon: Home },
      { id: "attendance", label: "Attendance", icon: CalendarDays },
    ];
    if (profile?.show_pay) out.push({ id: "pay", label: "Pay", icon: Wallet });
    out.push({ id: "leave", label: "Leave", icon: CalendarHeart });
    out.push({ id: "holidays", label: "Holidays", icon: PartyPopper });
    return out;
  }, [profile?.show_pay]);

  React.useEffect(() => {
    if (!items.some((i) => i.id === section)) setSection("today");
  }, [items, section]);

  const select = (id: string) => {
    setSection(id as Section);
    window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
  };

  const ready = profile && profile.is_team_member && profile.module_enabled;

  let content: React.ReactNode;
  if (profileQuery.isLoading) {
    content = (
      <SectionCard className="space-y-3" aria-hidden="true">
        <div className="bg-muted h-6 w-40 rounded motion-safe:animate-pulse" />
        <div className="bg-muted h-12 w-full rounded-2xl motion-safe:animate-pulse" />
      </SectionCard>
    );
  } else if (profileQuery.isError) {
    content = (
      <SectionCard role="alert" className="flex items-center justify-between gap-3 text-sm">
        <span className="text-destructive">Couldn&apos;t load your team profile.</span>
        <Button size="sm" variant="outline" onClick={() => profileQuery.refetch()}>
          Retry
        </Button>
      </SectionCard>
    );
  } else if (!ready) {
    content = (
      <NotSetUp
        reason={profile?.is_team_member ? "module" : "member"}
        onSignOut={() => void signOut()}
      />
    );
  } else {
    content = (
      <motion.div
        key={section}
        initial={reduce ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: EASE_OUT }}
      >
        {section === "today" && <TeamToday profile={profile} firstName={firstName} />}
        {section === "attendance" && <TeamAttendance />}
        {section === "pay" && profile.show_pay && <TeamPay />}
        {section === "leave" && <TeamLeaves allowRequests={profile.allow_leave_requests} />}
        {section === "holidays" && <TeamHolidays />}
      </motion.div>
    );
  }

  return (
    <div className="portal-aurora bg-background text-foreground relative flex min-h-dvh">
      {ready ? (
        <SideNav
          institution={institution}
          items={items}
          activeId={section}
          onSelect={select}
          learnerLocale={false}
        />
      ) : null}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="border-border/50 bg-background/80 sticky top-0 z-20 border-b backdrop-blur-lg lg:hidden">
          <div className="mx-auto flex max-w-md items-center justify-between px-4 py-3 sm:max-w-xl md:max-w-3xl">
            <div className="flex items-center gap-2.5">
              <InstitutionMark vertical={institution.vertical} name={institution.name} size="sm" />
              <div className="leading-tight">
                <p className="text-sm font-semibold">{institution.shortName}</p>
                <p className="text-muted-foreground text-[0.7rem]">
                  {verticalLabel(institution.vertical)} · Team
                </p>
              </div>
            </div>
            {ready ? (
              <Button
                variant="ghost"
                size="icon"
                className="size-9 rounded-full"
                aria-label="Sign out"
                onClick={() => void signOut()}
              >
                <LogOut aria-hidden="true" className="size-4" />
              </Button>
            ) : null}
          </div>
        </header>
        <main
          className={cn(
            "mx-auto w-full max-w-md px-4 pt-5 sm:max-w-xl md:max-w-2xl md:px-6 lg:max-w-3xl lg:px-8 lg:pt-8",
            ready ? "pb-28 lg:pb-12" : "pb-12",
          )}
        >
          {content}
        </main>
      </div>
      {ready ? (
        <div className="lg:hidden">
          <BottomNav items={items} activeId={section} onSelect={select} learnerLocale={false} />
        </div>
      ) : null}
    </div>
  );
}
